import React, { useState } from 'react';
import { Tree } from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  HomeOutlined,
  RightOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import ScreenTitleIcon from 'assets/icon/dynamic/ScreenTitleIcon';
import './FolderTree.scss';

export default function FolderTree({
  folders = [],
  screens = [],
  currentFolder,
  onFolderSelect,
  onCheck = () => {},
  checkKeys = [],
  halfCheckKeys = [],
  showRoot = true,
  showDetailLeaf = false,
  checkable = false,
  onFolderMove = () => {},
  enableDragDrop = false,
}) {
  const [expandedKeys, setExpandedKeys] = useState(['root']);

  // Auto-expand tree to show current folder
  React.useEffect(() => {
    if (currentFolder) {
      const folder = folders.find((f) => f._id === currentFolder);
      if (folder && folder.ancestors) {
        // Handle ancestors as objects or strings
        const ancestorIds = folder.ancestors.map(
          (ancestor) => ancestor?._id || ancestor
        );
        const keysToExpand = ['root', ...ancestorIds, currentFolder];
        setExpandedKeys(keysToExpand);
      }
    }
  }, [currentFolder, folders]);

  // Build tree structure from flat folder list
  const buildTreeData = () => {
    const treeData = [];

    if (showRoot) {
      const rootFolders = buildFolderTree(null);
      const rootScreens = getScreensForFolder(null);
      const rootChildren = [...rootFolders, ...rootScreens];

      treeData.push({
        title: enableDragDrop ? (
          <DroppableTreeNode folderId="root" folderName="All Screens">
            <span className="tree-row">All Screens</span>
          </DroppableTreeNode>
        ) : (
          'All Screens'
        ),
        key: 'root',
        icon: <HomeOutlined />,
        children: rootChildren,
      });
    } else {
      const rootFolders = buildFolderTree(null);
      const rootScreens = getScreensForFolder(null);
      return [...rootFolders, ...rootScreens];
    }

    return treeData;
  };

  const buildFolderTree = (parentId) => {
    // Get folders for this level
    const foldersAtLevel = folders
      .filter((folder) => {
        // Handle parentId as object or string
        const folderParentId = folder.parentId?._id || folder.parentId;
        return folderParentId === parentId;
      })
      .map((folder) => {
        const subFolders = buildFolderTree(folder._id);
        const screensInFolder = getScreensForFolder(folder._id);
        const children = [...subFolders, ...screensInFolder];
        const isLeaf = children.length === 0;

        return {
          key: folder._id,
          isLeaf,
          title: enableDragDrop ? (
            <DroppableTreeNode folderId={folder._id} folderName={folder.name}>
              <DraggableTreeNode
                id={folder._id}
                folder={folder}
                showDetailLeaf={showDetailLeaf}
                onFolderSelect={onFolderSelect}
              />
            </DroppableTreeNode>
          ) : (
            <span className="tree-row">
              {folder.name}
              {showDetailLeaf && isLeaf && (
                <a
                  onClick={(e) => {
                    e.stopPropagation();
                    onFolderSelect(folder._id, { details: true });
                  }}
                  style={{ fontSize: 12 }}
                >
                  Details <RightOutlined />
                </a>
              )}
            </span>
          ),
          icon: ({ expanded }) =>
            !isLeaf ? (
              expanded ? (
                <FolderOpenOutlined />
              ) : (
                <FolderOutlined />
              )
            ) : (
              <FolderOutlined />
            ),
          ...(children.length > 0 ? { children } : {}),
        };
      });

    return foldersAtLevel;
  };

  // Get screens for a specific folder
  const getScreensForFolder = (folderId) => {
    return screens
      .filter((screen) => {
        const screenParentId = screen.parentId?._id || screen.parentId;
        return screenParentId === folderId;
      })
      .map((screen) => ({
        key: screen._id,
        isLeaf: true,
        title: (
          <span className="tree-row tree-screen">
            {screen.name}
          </span>
        ),
        icon: <ScreenTitleIcon size={16} color="#6b7280" />,
        selectable: false, // Screens are not selectable in tree navigation
      }));
  };

  const handleSelect = (selectedKeys) => {
    if (selectedKeys.length > 0) {
      const key = selectedKeys[0];
      onFolderSelect(key === 'root' ? null : key);
    }
  };

  const handleExpand = (expandedKeys) => {
    setExpandedKeys(expandedKeys);
  };

  const selectedKeys = currentFolder ? [currentFolder] : ['root'];

  if (enableDragDrop) {
    return (
      <div className="folder-tree">
        <Tree
          treeData={buildTreeData()}
          expandedKeys={expandedKeys}
          selectedKeys={selectedKeys}
          checkedKeys={{ checked: checkKeys, halfChecked: halfCheckKeys }}
          onSelect={handleSelect}
          onExpand={handleExpand}
          checkable={checkable}
          onCheck={onCheck}
          showIcon
          className="custom-tree"
        />
      </div>
    );
  }

  return (
    <div className="folder-tree">
      <Tree
        treeData={buildTreeData()}
        expandedKeys={expandedKeys}
        selectedKeys={selectedKeys}
        checkedKeys={{ checked: checkKeys, halfChecked: halfCheckKeys }}
        onSelect={handleSelect}
        onExpand={handleExpand}
        checkable={checkable}
        onCheck={onCheck}
        showIcon
        className="custom-tree"
      />
    </div>
  );
}

// Draggable Tree Node Component
function DraggableTreeNode({ id, folder, showDetailLeaf, onFolderSelect }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: id,
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 1000 : 'auto',
        opacity: isDragging ? 0 : 1, // Hide original when dragging
      }
    : undefined;

  return (
    <span
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`tree-row ${isDragging ? 'dragging' : ''}`}
    >
      {folder.name}
      {showDetailLeaf && (
        <a
          onClick={(e) => {
            e.stopPropagation();
            onFolderSelect(folder._id, { details: true });
          }}
          style={{ fontSize: 12 }}
        >
          Details <RightOutlined />
        </a>
      )}
    </span>
  );
}

// Individual droppable tree node wrapper
function DroppableTreeNode({ folderId, folderName, children }) {
  const { setNodeRef, isOver, active } = useDroppable({
    id: `tree-folder-${folderId}`, // Make ID unique to avoid conflicts
    data: {
      type: 'folder',
      folderId: folderId,
      folderName: folderName,
    },
  });

  // `isOver` alone is true on plain hover; gating on `active` means the row
  // only highlights while something is actually being dragged.
  const showDropTarget = isOver && active;

  return (
    <div
      ref={setNodeRef}
      className={`tree-node-wrapper ${
        showDropTarget ? 'tree-drop-target' : ''
      }`}
      data-folder-id={folderId}
      data-folder-name={folderName}
      style={{
        width: '100%',
        minHeight: '32px',
        display: 'block', // Change from flex to block to avoid overlap issues
        transition: 'all 0.2s ease',
        position: 'relative',
        zIndex: showDropTarget ? 100 : 'auto',
        pointerEvents: 'auto',
      }}
    >
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        width: '100%',
        minHeight: '32px'
      }}>
        {children}
      </div>
    </div>
  );
}

FolderTree.propTypes = {
  folders: PropTypes.array,
  screens: PropTypes.array,
  currentFolder: PropTypes.string,
  onFolderSelect: PropTypes.func.isRequired,
  showRoot: PropTypes.bool,
  onFolderMove: PropTypes.func,
  enableDragDrop: PropTypes.bool,
};
